/* eslint react-hooks/exhaustive-deps: "error" */
import React from 'react';
import compareAsc from 'date-fns/compare_asc';
import format from 'date-fns/format';
import { css } from 'styled-components';
import { animated, useSpring } from 'react-spring';
import { useMutation, useApolloClient, useLazyQuery } from '@apollo/client';

import { matchMediaQuery } from '@crehana/utils';
import { useCountdown } from '@crehana/react-hooks';

import {
  colors,
  Tooltip,
  Dialog,
  DialogContent,
  PrimaryButton,
  InverseButton,
} from '@crehana/ui';

import {
  Clock,
  ArrowRight,
  ArrowLeft,
  Fullscreen,
  FullscreenOff,
} from '@crehana/icons';

import PusherProvider from '@crehana/web/dist/ReactContexts/PusherContext';

import { useCrehanaLiveTranslations } from 'Jsx/CrehanaLive/hooks';

import { CREHANA_LIVE_AMPLITUDE_PAGE_NAME } from 'Jsx/CrehanaLive/constants';

import { CountryPrefix } from 'Jsx/global.d';
import { PusherTargetEnum, LiveTargetEnum } from 'Jsx/Types/Graphql/globals';

import { createUrlWithSourceQueryParams } from 'Jsx/Analytics/Amplitude';
import usePageView from 'Jsx/Analytics/UserActivity/usePageView';

import Media from 'Jsx/Shared/ui/Media';
import { useMe } from 'Jsx/ContextApis/AuthContext';

import REGISTER_LIVE_VIEWER from 'Jsx/CrehanaLive/graphql/RegisterLiveViewer.graphql';
import {
  RegisterLiveViewer,
  RegisterLiveViewerVariables,
} from 'Jsx/CrehanaLive/graphql/types/RegisterLiveViewer';
import GET_LIVE_DETAIL from 'Jsx/CrehanaLive/graphql/GetLiveDetail.graphql';
import {
  GetLiveDetail,
  GetLiveDetailVariables,
} from 'Jsx/CrehanaLive/graphql/types/GetLiveDetail';
import {
  LiveNode,
  LiveNode_moderators_edges as ModeratorEdges,
} from 'Jsx/CrehanaLive/graphql/types/LiveNode';
import GET_NEXT_LIVE from 'Jsx/CrehanaLive/graphql/GetNextLive.graphql';
import { GetNextLive } from 'Jsx/CrehanaLive/graphql/types/GetNextLive';

import usePopupChat from '../../hooks/usePopupChat';
import { TABS_WIDTH_LG, TABS_WIDTH_MD } from '../../constants';

import MembershipSnackbar from '../../components/MembershipSnackbar';
import Speakers from '../../components/Speakers';

import LiveTabs from './sections/LiveTabs';
import Player from './sections/Player';

interface DetailProps {
  live: LiveNode;
  enrollToLive?: (liveId: number) => Promise<void>;
  countryPrefix?: CountryPrefix;
}

interface NextLiveState {
  show: boolean;
  live: LiveNode | null;
}

const Detail: React.FC<DetailProps> = ({
  live,
  enrollToLive,
  countryPrefix,
}) => {
  const { t } = useCrehanaLiveTranslations();

  usePageView({ type: 'live', id: live.originalId! });
  const { me } = useMe();
  const [didFinishLive, setDidFinishLive] = React.useState(false);
  const [nextLiveDialog, setNextLiveDialog] = React.useState<NextLiveState>({
    show: false,
    live: null,
  });

  const [getNextLive] = useLazyQuery<GetNextLive>(GET_NEXT_LIVE, {
    fetchPolicy: 'network-only',
    onCompleted: ({ nextLives }) => {
      if (nextLives?.edges.length) {
        const nextLive = nextLives.edges[0]?.node;

        if (nextLive) {
          setNextLiveDialog({ show: true, live: nextLive });
        }
      }
    },
  });

  const [isOpenMenu, setIsOpenMenu] = React.useState(true);
  const liveCountdownToStart = useCountdown(new Date(live.startLive));
  const { matches } = matchMediaQuery('lg');
  const [registerLiveViewer] =
    useMutation<RegisterLiveViewer, RegisterLiveViewerVariables>(
      REGISTER_LIVE_VIEWER,
    );
  const client = useApolloClient();

  const { close, isOpen: isOpenPopup, open } = usePopupChat();

  const openWidth = matches ? TABS_WIDTH_LG : TABS_WIDTH_MD;

  const tabsStyles = useSpring({
    width: isOpenMenu ? openWidth : '0px',
    opacity: isOpenMenu ? 1 : 0,
  });

  // it should be true if we are between the start and due date
  const now = new Date();
  const isInThePast = compareAsc(now, live.dueLive) === 1;
  const isFuture = compareAsc(live.startLive, now) === 1;

  const isActive =
    compareAsc(now, live.startLive) === 1 &&
    compareAsc(now, live.dueLive) === -1;
  const canSendMessages = !isInThePast;
  const chatIsActive = liveCountdownToStart.rawHours <= 0;

  // TODO: remove after event ends
  React.useEffect(() => {
    if (didFinishLive && live.tags?.includes('live-day-adobe')) {
      getNextLive();
    }
  }, [didFinishLive, getNextLive, live.tags]);

  React.useEffect(() => {
    if (compareAsc(live.dueLive, Date.now()) === -1) {
      setDidFinishLive(true);
    }
  }, [live.dueLive]);

  React.useEffect(() => {
    if (
      me &&
      live.originalId &&
      live.slug &&
      !live.userIsEnrolled &&
      (live.isLive || isActive)
    ) {
      registerLiveViewer({ variables: { liveId: live.originalId } })
        .then(({ data }) => {
          if (data?.liveEnrollment?.success) {
            const prevLiveDetail = client.readQuery<
              GetLiveDetail,
              GetLiveDetailVariables
            >({
              query: GET_LIVE_DETAIL,
              variables: { slug: live.slug },
            });

            if (prevLiveDetail?.live) {
              client.writeQuery<GetLiveDetail, GetLiveDetailVariables>({
                query: GET_LIVE_DETAIL,
                variables: { slug: live.slug },
                data: {
                  ...prevLiveDetail,
                  live: {
                    ...prevLiveDetail.live,
                    userIsEnrolled: true,
                  },
                },
              });
            }
          }
        })
        .catch(error => {
          if (window.Sentry) {
            window.Sentry.captureMessage('CrehanaLive: failed to enroll user', {
              error,
            });
          }
        });
    }
  }, [
    client,
    isActive,
    live.isLive,
    live.originalId,
    live.slug,
    live.userIsEnrolled,
    me,
    registerLiveViewer,
  ]);

  const hasChatPopupPermission = React.useMemo(() => {
    if (!me) {
      return false;
    }
    if (me.isStaff) {
      return true;
    }
    const userId = me.originalId;
    // The type for moderators and presenters is the same (UserNode)
    const search = (edge: ModeratorEdges | null) =>
      edge!.node!.originalId === userId;

    const isMod = Boolean(live.moderators?.edges.find(search));

    if (isMod) {
      return true;
    }

    // Presenters also have moderator permissions
    const isPresenter =
      live.presenter?.originalId === me.originalId ||
      Boolean(live.presenters?.edges.find(search));

    if (isPresenter) {
      return true;
    }

    return false;
  }, [live.moderators, live.presenter, live.presenters, me]);

  const isForbiddenPremiumLive =
    live.targetEnum === LiveTargetEnum.TARGET_PREMIUM && !me?.isPremiumMember;
  const premiumLandingUrl = createUrlWithSourceQueryParams({
    url: countryPrefix ? `/${countryPrefix}/premium/` : '/premium/',
    source: CREHANA_LIVE_AMPLITUDE_PAGE_NAME,
    sourceDetail: 'Premium Live',
  });

  return (
    <PusherProvider
      target={PusherTargetEnum.LIVE}
      skip={isInThePast || !chatIsActive || isForbiddenPremiumLive}
    >
      <div className="flex flex-col flex-no-wrap w-screen h-full overflow-hidden md:flex-row">
        <div className="w-full">
          <Player
            live={live}
            liveCountdownToStart={liveCountdownToStart}
            liveIsActive={isActive && liveCountdownToStart.finished}
            isInThePast={isInThePast}
            isFuture={isFuture}
            fullWidth={!isOpenMenu}
            enrollToLive={enrollToLive}
            setDidFinishLive={setDidFinishLive}
            isForbiddenPremiumLive={isForbiddenPremiumLive}
            premiumLandingUrl={premiumLandingUrl}
          />
        </div>

        <Media from="xs" until="md">
          <LiveTabs
            liveIsActive={isActive && liveCountdownToStart.finished}
            chatIsActive={chatIsActive}
            canSendMessages={canSendMessages}
            isFuture={isFuture}
            live={live}
            enrollToLive={enrollToLive}
            isForbiddenPremiumLive={isForbiddenPremiumLive}
          />
        </Media>

        <Media from="md">
          <animated.div
            className="relative"
            style={{ width: tabsStyles.width }}
          >
            {isActive ? (
              <>
                <Tooltip
                  label={
                    isOpenMenu ? t('HIDDEN_CHAT_LABEL') : t('SHOW_CHAT_LABEL')
                  }
                  position="left"
                >
                  <button
                    type="button"
                    className="absolute hidden p-4 outline-none bg-base-main md:block"
                    style={{ left: '-32px', lineHeight: 0 }}
                    onClick={() => setIsOpenMenu(prev => !prev)}
                  >
                    {isOpenMenu ? (
                      <ArrowRight size={24} color={colors.white} />
                    ) : (
                      <ArrowLeft size={24} color={colors.white} />
                    )}
                  </button>
                </Tooltip>
                {hasChatPopupPermission ? (
                  <Tooltip
                    label={
                      isOpenMenu
                        ? t('POP_UP_CHAT_LABEL')
                        : t('HIDDEN_POP_UP_CHAT_LABEL')
                    }
                    position="left"
                  >
                    <button
                      type="button"
                      className="absolute hidden p-4 outline-none bg-base-main md:block"
                      style={{ left: '-32px', top: 32, lineHeight: 0 }}
                      onClick={() => (isOpenPopup ? close() : open())}
                    >
                      {isOpenPopup ? (
                        <FullscreenOff size={24} color={colors.white} />
                      ) : (
                        <Fullscreen size={24} color={colors.white} />
                      )}
                    </button>
                  </Tooltip>
                ) : null}
              </>
            ) : null}
            <animated.div
              style={{ opacity: tabsStyles.opacity }}
              // @ts-ignore
              css={css`
                ${props => props.theme.breakpoints.up('md')} {
                  height: calc(100% - 54px);
                  width: ${TABS_WIDTH_MD};
                }
                ${props => props.theme.breakpoints.up('lg')} {
                  width: ${TABS_WIDTH_LG};
                }
              `}
            >
              <LiveTabs
                live={live}
                liveIsActive={isActive && liveCountdownToStart.finished}
                chatIsActive={chatIsActive}
                canSendMessages={canSendMessages}
                isPoppedOut={isOpenPopup}
                closePopup={close}
                isForbiddenPremiumLive={isForbiddenPremiumLive}
              />
            </animated.div>
          </animated.div>
        </Media>
        {!isInThePast && !me?.hasOrganization && !me?.isPremiumMember && (
          <MembershipSnackbar
            liveCountdownToStart={liveCountdownToStart}
            isInThePast={isInThePast}
          />
        )}
      </div>

      <Dialog
        open={nextLiveDialog.show}
        onDismiss={() => setNextLiveDialog(prev => ({ ...prev, show: false }))}
        dark
      >
        <DialogContent dark>
          {nextLiveDialog.live ? (
            <>
              <h3 className="font-h2 text-white mb-24 sm:mb-48 text-center">
                {t('NOT_MISS_LIVE')}
              </h3>

              <div className="flex flex-col lg:flex-row w-full mb-8 lg:mb-48">
                <div>
                  <img
                    src={nextLiveDialog.live.coverCard!}
                    alt={nextLiveDialog.live.title}
                    className="w-full lg:w-160 h-full object-cover"
                  />
                </div>

                <div className="lg:bg-base-lighter-16 py-16 lg:px-24 w-full">
                  <p className="font-label text-secondary-main mb-8 truncate">
                    {nextLiveDialog.live.liveCategory?.name}
                  </p>

                  <p className="font-subtitle3 text-white mb-16">
                    {nextLiveDialog.live.title}
                  </p>

                  <div className="flex items-center">
                    <Speakers
                      size={16}
                      presenters={nextLiveDialog.live.presenters}
                      className="text-gray-dark"
                    />

                    <Clock
                      size={16}
                      color={colors.baseLighter}
                      className="mr-8"
                    />
                    <p className="font-caption text-base-lighter">
                      {format(nextLiveDialog.live.startLive, 'HH:mm [hrs.]')}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col-reverse lg:flex-row lg:justify-center">
                <InverseButton
                  gray
                  label={t('SEE_PROGRAMMING_LABEL')}
                  isDark
                  className="lg:mr-16"
                  href="/live-day/"
                />
                {nextLiveDialog.live.calendarLink && (
                  <PrimaryButton
                    label={t('SHEDULE_CALENDAR_LABEL')}
                    isDark
                    href={nextLiveDialog.live.calendarLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mb-16 lg:mb-0"
                  />
                )}
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </PusherProvider>
  );
};

export default Detail;
